import React, { useState } from 'react';
import { Loader2, CreditCard, Settings, Zap, Ticket, Check, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

const TIER_ORDER = ['free', 'starter', 'pro', 'business', 'enterprise'];

const PLAN_DETAILS = {
  free:       { name: 'Free',       price: '$0',       period: '',        credits: '200/quarter', seats: '1 user', orgs: '1 org',  features: ['PDF, CSV & image uploads', 'AI classification', 'Basic reports'] },
  starter:    { name: 'Starter',    price: '$9.99',    period: '/seat/mo', credits: '500/mo',      seats: 'Unlimited', orgs: '1 org',  features: ['Everything in Free', 'Credit top-ups', 'Overage at $0.07/cr', '30 chat messages/mo'] },
  pro:        { name: 'Pro',        price: '$29.99',   period: '/seat/mo', credits: '2,000/mo',    seats: 'Unlimited', orgs: '3 orgs', features: ['Everything in Starter', 'Overage at $0.05/cr', '100 chat messages/mo'] },
  business:   { name: 'Business',   price: '$99.99',   period: '/seat/mo', credits: '7,000/mo',    seats: 'Unlimited', orgs: '5 orgs', features: ['Everything in Pro', 'Priority processing', 'Overage at $0.03/cr', '500 chat messages/mo'] },
  enterprise: { name: 'Enterprise', price: '$199.99',  period: '/seat/mo', credits: '30,000/mo',   seats: 'Unlimited', orgs: '10 orgs', features: ['Everything in Business', 'Overage at $0.02/cr', '2,000 chat messages/mo'] },
};

export default function SubscriptionTab({
  creditInfo, org, checkoutLoading, checkoutResult, setCheckoutResult,
  couponCode, setCouponCode, redeemingCoupon,
  handleUpgrade, handleDowngrade, handleBuyCredits, handleRedeemCoupon,
  handleManageBilling, handleCancelSubscription, handleReactivate,
}) {
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const currentTier = creditInfo?.tier || 'free';
  const currentIndex = TIER_ORDER.indexOf(currentTier);

  const handleDowngradeConfirm = () => {
    if (!downgradeTarget) return;
    if (downgradeTarget === 'free') {
      handleCancelSubscription();
    } else {
      handleDowngrade(downgradeTarget);
    }
    setDowngradeTarget(null);
  };

  return (
    <div className="space-y-6">
      {/* Checkout success banner */}
      {checkoutResult?.status === 'paid' && (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="checkout-success-banner">
          <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800">
              {checkoutResult.type === 'topup'
                ? `${checkoutResult.credits} credits have been added to your account.`
                : `Your plan has been upgraded to ${checkoutResult.tier}.`}
            </p>
          </div>
          <button onClick={() => setCheckoutResult(null)} className="ml-auto text-emerald-400 hover:text-emerald-600 text-sm">&times;</button>
        </div>
      )}

      {/* Current Plan Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#D94E18]" /> Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          {creditInfo ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {creditInfo.tier_config?.display_name || creditInfo.tier}
                    </span>
                    <Badge variant="outline" className={
                      currentTier === 'pro' ? 'border-[#D94E18] text-[#D94E18]' :
                      currentTier === 'business' ? 'border-violet-600 text-violet-600' :
                      currentTier === 'enterprise' ? 'border-emerald-600 text-emerald-600' :
                      currentTier === 'starter' ? 'border-blue-500 text-blue-600' :
                      'border-zinc-400 text-zinc-500'
                    }>
                      {creditInfo.tier_config?.price_label || '$0/mo'}
                    </Badge>
                    {currentTier !== 'free' && creditInfo.seat_count > 1 && (
                      <span className="text-sm text-zinc-400 ml-1">
                        x {creditInfo.seat_count} seats
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500 mt-1">
                    {currentTier === 'free' ? (
                      <>{creditInfo.tier_config?.monthly_credits?.toLocaleString()} credits/quarter</>
                    ) : (
                      <>{creditInfo.seat_count || 1} seat{(creditInfo.seat_count || 1) > 1 ? 's' : ''} x {creditInfo.credits_per_seat?.toLocaleString()} credits = {creditInfo.monthly_allocation?.toLocaleString()} credits/mo</>
                    )}
                  </p>
                </div>
              </div>

              {/* Credit usage bar */}
              <div className="p-4 bg-zinc-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#D94E18]" />
                    <span className="text-sm font-medium text-zinc-700">Credits</span>
                  </div>
                  <span className="text-sm text-zinc-500">
                    Resets {creditInfo.reset_date ? new Date(creditInfo.reset_date).toLocaleDateString() : (currentTier === 'free' ? 'quarterly' : 'monthly')}
                  </span>
                </div>
                <div className="w-full h-2.5 bg-zinc-200 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      creditInfo.monthly_used >= creditInfo.monthly_allocation ? 'bg-red-500' :
                      creditInfo.monthly_used >= creditInfo.monthly_allocation * 0.8 ? 'bg-amber-500' : 'bg-[#D94E18]'
                    }`}
                    style={{ width: `${Math.min(100, (creditInfo.monthly_used / Math.max(1, creditInfo.monthly_allocation)) * 100)}%` }}
                    data-testid="settings-credit-bar"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">{creditInfo.monthly_used} / {creditInfo.monthly_allocation} used</span>
                  <span className="font-medium text-zinc-700">{creditInfo.total_available} available</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">Loading subscription data...</p>
          )}
        </CardContent>
      </Card>

      {/* Scheduled change banner */}
      {org?.scheduled_tier && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="scheduled-change-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Your plan will change to <strong>{org.scheduled_tier}</strong> at the end of this billing period.
          </p>
          <Button variant="outline" size="sm" onClick={handleReactivate} disabled={!!checkoutLoading} className="ml-auto text-xs" data-testid="reactivate-btn">
            Keep current plan
          </Button>
        </div>
      )}

      {/* Plan Comparison Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Choose Your Plan</CardTitle>
          <CardDescription>Upgrade for more credits and features, or downgrade anytime.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plan-grid">
            {TIER_ORDER.map((tier, index) => {
              const plan = PLAN_DETAILS[tier];
              const isCurrent = tier === currentTier;
              const isUpgrade = index > currentIndex;
              const isDowngrade = index < currentIndex;

              return (
                <div
                  key={tier}
                  className={`relative rounded-xl border-2 p-5 transition-all ${
                    isCurrent
                      ? 'border-[#D94E18] bg-orange-50/50 ring-1 ring-[#D94E18]/20'
                      : 'border-zinc-200 hover:border-zinc-300 bg-white'
                  }`}
                  data-testid={`plan-card-${tier}`}
                >
                  {isCurrent && (
                    <Badge className="absolute -top-2.5 left-4 bg-[#D94E18] text-white text-[10px]">Current Plan</Badge>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-0.5 mt-1">
                      <span className="text-2xl font-bold text-zinc-900">{plan.price}</span>
                      {plan.period && <span className="text-sm text-zinc-400">{plan.period}</span>}
                    </div>
                  </div>

                  <div className="space-y-2 mb-5">
                    <div className="flex items-center gap-2 text-sm">
                      <Zap className="w-3.5 h-3.5 text-[#D94E18] flex-shrink-0" />
                      <span className="text-zinc-700 font-medium">{plan.credits} credits</span>
                    </div>
                    <div className="text-xs text-zinc-500 pl-5.5 space-y-1">
                      <p>{plan.seats} team members</p>
                      <p>{plan.orgs}</p>
                    </div>
                  </div>

                  <ul className="space-y-1.5 mb-5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-zinc-600">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action button */}
                  {isCurrent ? (
                    <Button variant="outline" size="sm" className="w-full" disabled>
                      Current Plan
                    </Button>
                  ) : isUpgrade ? (
                    <Button
                      size="sm"
                      className="w-full bg-[#D94E18] hover:bg-[#C2410C] text-white"
                      disabled={!!checkoutLoading}
                      onClick={() => handleUpgrade(tier)}
                      data-testid={`upgrade-to-${tier}-btn`}
                    >
                      {checkoutLoading === `subscribe-${tier}` ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      ) : (
                        <ArrowUp className="w-4 h-4 mr-1.5" />
                      )}
                      Upgrade
                    </Button>
                  ) : isDowngrade ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-zinc-600 hover:text-zinc-800"
                      disabled={!!checkoutLoading}
                      onClick={() => setDowngradeTarget(tier)}
                      data-testid={`downgrade-to-${tier}-btn`}
                    >
                      {checkoutLoading === `downgrade-${tier}` ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      ) : (
                        <ArrowDown className="w-4 h-4 mr-1.5" />
                      )}
                      Downgrade
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-zinc-400 mt-4">
            Upgrades take effect immediately (prorated). Downgrades are scheduled for the end of your current billing period.
          </p>
        </CardContent>
      </Card>

      {/* Manage Subscription (existing subscribers) */}
      {creditInfo && currentTier !== 'free' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#D94E18]" /> Manage Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" onClick={handleManageBilling} data-testid="manage-billing-btn">
                <CreditCard className="w-4 h-4 mr-1.5" />Update payment method
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancelSubscription} disabled={!!checkoutLoading} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-subscription-btn">
                {checkoutLoading === 'cancel' && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                Cancel at period end
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Buy Credits */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D94E18]" /> Buy Credits
          </CardTitle>
          <CardDescription>Purchased credits never expire.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {[
              { credits: 50, price: '$5' },
              { credits: 150, price: '$12' },
              { credits: 500, price: '$30' },
            ].map((pack) => (
              <button
                key={pack.credits}
                data-testid={`buy-pack-${pack.credits}`}
                onClick={() => handleBuyCredits(pack.credits)}
                disabled={checkoutLoading === `topup-${pack.credits}`}
                className="p-4 rounded-lg border-2 border-zinc-200 hover:border-[#D94E18] bg-white text-center transition-colors group disabled:opacity-60 disabled:cursor-wait"
              >
                {checkoutLoading === `topup-${pack.credits}` ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#D94E18]" />
                ) : (
                  <>
                    <div className="text-2xl font-bold text-zinc-900 group-hover:text-[#D94E18] transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>{pack.credits}</div>
                    <div className="text-xs text-zinc-500 mb-2">credits</div>
                    <div className="text-sm font-medium text-[#D94E18]">{pack.price}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Redeem Coupon */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#D94E18]" /> Redeem Coupon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3" data-testid="coupon-redeem-section">
            <Input
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={e => setCouponCode(e.target.value.toUpperCase())}
              className="max-w-xs uppercase"
              data-testid="coupon-code-redeem-input"
            />
            <Button onClick={handleRedeemCoupon} disabled={redeemingCoupon || !couponCode.trim()} className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="redeem-coupon-btn">
              {redeemingCoupon && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              Redeem
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Downgrade Confirmation Dialog */}
      <AlertDialog open={!!downgradeTarget} onOpenChange={() => setDowngradeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Downgrade to {PLAN_DETAILS[downgradeTarget]?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {downgradeTarget === 'free' ? (
                <>Your subscription will be cancelled at the end of the current billing period. You'll keep your current plan benefits until then, after which you'll move to the Free plan.</>
              ) : (
                <>Your plan will change to <strong>{PLAN_DETAILS[downgradeTarget]?.name}</strong> at the end of your current billing period. You'll keep your current benefits until then. No prorated refunds are issued for downgrades.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Current Plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDowngradeConfirm}
              className="bg-zinc-900 hover:bg-zinc-800"
              data-testid="confirm-downgrade-btn"
            >
              Confirm Downgrade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
